import { addMonths, format, isAfter, startOfMonth } from "date-fns";
import { IconTypes } from "../components/ui/icons";
import { Activity } from "../models/activity";
import {
  ActivityType,
  ScheduledTrainingModule,
  SwimlanesData,
  TrainingModuleOption,
  todoLink,
} from "../types/actionPlan";
import { getFormattedDate } from "./formatters";
import { getDFLocale } from "./locale.utils";
import { ProtectedRoutes } from "./routes.utils";

const sortByDate = <T extends { startDate: Date }>(arr: T[]) =>
  [...arr].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

export const generateSwimlanesData = (
  activities: Activity[],
  t: (s: string) => string
): SwimlanesData => {
  const surveyActivities: Activity[] = [];
  const trainingGeneral: Activity[] = [];
  const trainingLeadership: Activity[] = [];

  for (const activity of activities) {
    const timelineActivity = activity.setUIFields();
    if (activity.isSurveyActivity()) {
      surveyActivities.push(timelineActivity);
    } else if (activity.activityType === ActivityType.Training) {
      trainingGeneral.push(timelineActivity);
    } else if (activity.activityType === ActivityType.LeadershipTraining) {
      trainingLeadership.push(timelineActivity);
    }
  }

  return {
    inclusionPulse: {
      title: t("timeline.inclusionPulse"),
      icon: IconTypes.Clipboard,
      activities: sortByDate(surveyActivities),
    },
    microLearning: {
      title: t("timeline.microLearning"),
      icon: IconTypes.Employee,
      activities: sortByDate(trainingGeneral),
    },
    leadershipLearning: {
      title: t("timeline.leadershipLearning"),
      icon: IconTypes.Management,
      activities: sortByDate(trainingLeadership),
    },
  };
};

export const todoLinkMappings: todoLink = {
  training: ProtectedRoutes.TRAINING,
  launch: ProtectedRoutes.LAUNCH,
  integrations: ProtectedRoutes.SETTINGS,
  inclusionIndex: ProtectedRoutes.WORKPLACE_CULTURE,
};

export const getRailLabels = (min: Date, max: Date, language: string) => {
  const labels: string[] = [];
  const locale = getDFLocale(language);
  let currentMonth = startOfMonth(min);
  const endMonth = startOfMonth(max);

  while (!isAfter(currentMonth, endMonth)) {
    labels.push(format(currentMonth, "MMM ''yy", { locale }));
    currentMonth = addMonths(currentMonth, 1);
  }

  return labels;
};

export const getTimelineRange = (
  activities: Activity[],
  now: Date,
  rangeLength: number = 6 // Number of months to show in timeline
): { startDate: Date; endDate: Date } => {
  if (
    !activities.length ||
    activities.some((activity) => activity.startDate < now)
  ) {
    return {
      startDate: startOfMonth(addMonths(now, -1)),
      endDate: startOfMonth(addMonths(now, rangeLength - 1)),
    };
  }
  // Find the earliest activity date
  const firstDate = new Date(
    Math.min(...activities.map((activity) => activity.startDate.getTime()))
  );
  return {
    startDate: startOfMonth(firstDate),
    endDate: startOfMonth(addMonths(firstDate, rangeLength)),
  };
};

export const getPositionFromDate = (date: Date, start: Date, end: Date) => {
  const totalRange = end.getTime() - start.getTime();
  const elapsed = date.getTime() - start.getTime();
  return (elapsed / totalRange) * 100;
};

export const getTooltipTitle = (
  label: string,
  t: (s: string) => string,
  date: Date,
  endDate?: Date,
  locale?: string
) => {
  const normalizedLocale = locale?.toLowerCase() ?? undefined;
  return endDate
    ? `${label} - ${getFormattedDate(
        date,
        t,
        normalizedLocale,
        "P"
      )} to ${getFormattedDate(endDate, t, normalizedLocale, "P")}`
    : `${label} - ${getFormattedDate(date, t, normalizedLocale, "P")}`;
};

/** Converts selected modules to API format */
export const toScheduledTrainingModules = (
  modules: TrainingModuleOption[]
): ScheduledTrainingModule[] =>
  modules
    .filter((m) => m.scheduledDate)
    .map((m) => ({
      training_module_id: m.trainingModule.referenceId,
      start_date: m.scheduledDate!.toISOString(),
    }));
